import { fallback } from "@tanstack/zod-adapter"
import { z } from "zod"

import { inventoryDestinationFormSchema } from "../../../schemas"
import { TransferProductCostCompleted, TransferReason, TransferSyncStatusFilter, TransferType } from "../constants"

import {
  type InventoryPalletGroup,
  type InventoryPalletLot,
  SortingOrder,
  TransferSortField,
  TransferStatus,
} from "@/graphql/codegen/graphql"

const transferLaunchFormBaseSchema = z.object({
  type: z.nativeEnum(TransferType),
  sourceWarehouseAreaId: z.string().min(1, "Source Warehouse is required"),
  targetWarehouseAreaId: z.string().optional(),
})

const validateTargetWarehouseArea = (
  { type, sourceWarehouseAreaId, targetWarehouseAreaId }: z.infer<typeof transferLaunchFormBaseSchema>,
  ctx: z.RefinementCtx
) => {
  if (type !== TransferType.InterWarehouse) return

  if (!targetWarehouseAreaId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["targetWarehouseAreaId"],
      message: "Target Warehouse is required",
    })
  } else if (targetWarehouseAreaId === sourceWarehouseAreaId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["targetWarehouseAreaId"],
      message: "Target Warehouse must differ from Source Warehouse",
    })
  }
}

export const transferLaunchFormSchema = transferLaunchFormBaseSchema.superRefine(validateTargetWarehouseArea)

export type TransferLaunchFormValues = z.infer<typeof transferLaunchFormSchema>

export const transferFormSchema = transferLaunchFormBaseSchema
  .extend({
    reason: z.nativeEnum(TransferReason).optional(),
    comment: z.string().max(50, "Comment must not exceed 50 characters").optional(),
    documents: z.array(z.object({ file: z.instanceof(File) })).optional(),
  })
  .superRefine(validateTargetWarehouseArea)

export type TransferFormValues = z.infer<typeof transferFormSchema>

export const lpnSchema = z.string().max(20, "LPN must not exceed 20 characters") // AS400 constraint

export const interTransferLotFormSchema = z
  .custom<InventoryPalletLot>()
  .and(z.object({ qtyToMove: z.number({ invalid_type_error: "Qty to Move is required" }).nullable() }))
  .superRefine(({ totalUnits, qtyToMove }, ctx) => {
    const availableQty = totalUnits ?? 0

    if (qtyToMove === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["qtyToMove"], message: "Qty to Move is required" })
    } else if (qtyToMove <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["qtyToMove"],
        message: "Qty to Move must be a positive number",
      })
    } else if (qtyToMove > availableQty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["qtyToMove"],
        message: `Qty to Move must be less than or equal to ${availableQty}`,
      })
    }
  })

export type InterTransferLotFormValues = z.infer<typeof interTransferLotFormSchema>

export const interTransferPalletFormSchema = z
  .custom<Omit<InventoryPalletGroup, "lots">>()
  .and(z.object({ lpn: lpnSchema, lots: z.array(interTransferLotFormSchema).min(1) }))

export type InterTransferPalletFormValues = z.infer<typeof interTransferPalletFormSchema>

export const interTransferPalletSelectionFormSchema = z.object({
  pallets: z.array(interTransferPalletFormSchema).min(1, "Select at least one pallet"),
})

export type InterTransferPalletSelectionFormValues = z.infer<typeof interTransferPalletSelectionFormSchema>

export const intraTransferPalletFormSchema = z
  .custom<InventoryPalletGroup>()
  .and(z.object({ destination: inventoryDestinationFormSchema }))

export type IntraTransferPalletFormValues = z.infer<typeof intraTransferPalletFormSchema>

export const intraTransferPalletSelectionFormSchema = z.object({
  pallets: z.array(intraTransferPalletFormSchema).min(1, "Select at least one pallet"),
})

export type IntraTransferPalletSelectionFormValues = z.infer<typeof intraTransferPalletSelectionFormSchema>

export const transferLpnFormSchema = z.object({
  pallets: z.array(z.object({ transferPalletId: z.string(), referenceNbr: z.number(), lpn: lpnSchema })),
})

export type TransferLpnFormValues = z.infer<typeof transferLpnFormSchema>

export const transferSearchSchema = z.object({
  query: z.string().optional(),
  field: fallback(z.nativeEnum(TransferSortField), TransferSortField.CreatedOn),
  order: fallback(z.nativeEnum(SortingOrder), SortingOrder.Desc),
  types: z.array(z.nativeEnum(TransferType)).optional(),
  // TODO(AK-6818): Use the generated status enum alone once the backend exposes AS400 sync statuses.
  statuses: z
    .array(z.nativeEnum({ ...TransferStatus, ...TransferSyncStatusFilter }))
    .transform((statuses) => statuses.filter((status) => status !== TransferStatus.Cancelled))
    .optional(),
  productCostCompleted: z.enum([TransferProductCostCompleted.Yes, TransferProductCostCompleted.No]).optional(),
  sourceWarehouseAreaIds: z.array(z.string()).optional(),
  targetWarehouseAreaIds: z.array(z.string()).optional(),
  createdByUserIds: z.array(z.string()).optional(),
  createdOnFrom: z.string().nullish(),
  createdOnTo: z.string().nullish(),
})

export type TransferSearchParams = z.infer<typeof transferSearchSchema>
